import { useMemo, useState, type RefObject } from 'react';
import { Highlighter } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { HighlightComposer, type ComposerSource } from '@/components/HighlightComposer';
import { useTextSelection } from '@/hooks/useTextSelection';
import { mapSelectionToQuote, type MappedQuote } from '@/lib/textMatch';

interface SelectionHighlighterProps {
  containerRef: RefObject<HTMLElement | null>;
  /** The source document text, used to map the selection back to source form. */
  sourceContent: string;
  source: ComposerSource;
  onPublished?: (event: NostrEvent) => void;
}

/**
 * Shows a floating "Highlight" button when the user selects text inside the
 * container, opening the highlight composer with the mapped quote + context.
 */
export function SelectionHighlighter({ containerRef, sourceContent, source, onPublished }: SelectionHighlighterProps) {
  const { selection, clearSelection } = useTextSelection(containerRef);
  const [composerOpen, setComposerOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<MappedQuote | null>(null);

  const mapped = useMemo(
    () => (selection ? mapSelectionToQuote(selection.text, sourceContent) : null),
    [selection, sourceContent],
  );

  const openComposer = () => {
    setSnapshot(mapped);
    setComposerOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    setComposerOpen(open);
    if (!open) {
      setSnapshot(null);
      clearSelection();
    }
  };

  // Position above the selection, clamped to the viewport.
  const style = selection
    ? {
        top: Math.max(8, selection.rect.top - 48),
        left: Math.min(Math.max(selection.rect.left + selection.rect.width / 2, 96), window.innerWidth - 96),
      }
    : undefined;

  return (
    <>
      {selection && !composerOpen && style && (
        <button
          type="button"
          data-highlight-action
          // Prevent the mousedown from collapsing the selection before click.
          onMouseDown={(e) => e.preventDefault()}
          onClick={openComposer}
          style={style}
          className="fixed z-50 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        >
          <Highlighter className="size-4" />
          Highlight
        </button>
      )}

      <HighlightComposer
        open={composerOpen}
        onOpenChange={handleOpenChange}
        source={source}
        initialQuote={snapshot?.quote}
        initialContext={snapshot?.context}
        onPublished={onPublished}
      />
    </>
  );
}
